import { VisuallyHidden } from '@ariakit/react';
import {
  Root,
  Portal,
  Overlay,
  Content,
  Description,
  Title,
} from '@radix-ui/react-dialog';
import { keyframes, styled } from 'styled-components';

const overlayShow = keyframes`
from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
  `;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }`;

const DialogOverlay = styled(Overlay)`
  background-color: #111418b3;
  position: fixed;
  inset: 0;
  animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const DialogContent = styled(Content)`
  background-color: white;
  color: #1c2127;
  border-radius: 6px;
  box-shadow:
    hsl(206deg 22% 7% / 35%) 0 10px 38px -10px,
    hsl(206deg 22% 7% / 20%) 0 10px 20px -15px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 450px;
  max-height: 85vh;
  padding: 15px;
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);

  > form {
    display: flex;
    flex-direction: column;
  }
`;

/**
 * The dialog's header
 */
export const DialogHeader = styled.header`
  margin: 0;
  font-weight: 500;
  font-size: 18px;
`;

/**
 * Wrapper for the dialog's action buttons
 */
export const DialogActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 36px;
  gap: 12px;
`;

const $root = document.querySelector('#portal-root');

/**
 * A controlled dialog component
 *
 * @param {object} props -
 * @param {string} props.name - The accessible name of the dialog.
 * @param {string} props.description - The accessible description of the dialog.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onOpenChange - The function to call when the opened state of dialog changes.
 * @param {ReactNode} props.children - The content of the dialog.
 * @returns {ReactNode} The dialog component.
 */
export default function Dialog({
  name,
  description,
  open,
  onOpenChange,
  children,
}) {
  return (
    <Root open={open} onOpenChange={onOpenChange}>
      <Portal container={$root}>
        <DialogOverlay />
        <DialogContent>
          <VisuallyHidden>
            <Title
            /**
             * A title and description are required for accessibility purposes.
             * These are hidden visually but read by screen readers.
             */
            >
              {name}
            </Title>
            <Description>{description}</Description>
          </VisuallyHidden>
          {children}
        </DialogContent>
      </Portal>
    </Root>
  );
}
